/**
 * Browser download of the exports: the 2D plan (DXF, SVG) and the 3D model
 * (STEP, STL) of a room, or of a whole assembled level, serialized in memory
 * and delivered via a temporary link click. The 3D models, the file names and
 * the content types are editor-core's, shared with Kai Pro; the 2D plan is
 * drawn by this package's plan renderer.
 */

import { FloorPlanRoom } from '@kaiizen/kai-floor-plan';
import {
  type AssemblyRoom,
  DEFAULT_FLOOR_PLAN_PREFERENCES,
  exportFile,
  type ExportFormat,
  levelExportName,
  levelModelContent,
  type PlanLayers,
  type PlanViewPreferences,
  ROOM_EXPORT_NAME,
  roomModelContent,
} from '@kaiizen/kai-floor-plan-editor-core';
import { assemblyToPlanDxf, assemblyToPlanSvg, downloadFile, roomToPlanDxf, roomToPlanSvg } from './components';

const ROOM_SERIALIZERS: Record<ExportFormat, (room: FloorPlanRoom, layers: PlanLayers) => string | ArrayBuffer> = {
  dxf: roomToPlanDxf,
  svg: roomToPlanSvg,
  step: (room, layers) => roomModelContent(room, 'step', layers),
  stl: (room, layers) => roomModelContent(room, 'stl', layers),
};

const ASSEMBLY_SERIALIZERS: Record<ExportFormat, (rooms: readonly AssemblyRoom[], view: PlanViewPreferences) => string | ArrayBuffer> = {
  dxf: assemblyToPlanDxf,
  svg: assemblyToPlanSvg,
  step: (rooms, view) => levelModelContent(rooms, 'step', view.layers),
  stl: (rooms, view) => levelModelContent(rooms, 'stl', view.layers),
};

/**
 * The room serialized for export. `layers` mirrors the view menu so
 * the file shows exactly what's on screen: the 2D exports take the same
 * bare-floor branch the plan renders, and the 3D exports drop free elements
 * the same way View3DCanvas does.
 */
export const serializeRoomModel = (room: FloorPlanRoom, format: ExportFormat, layers: PlanLayers): string | ArrayBuffer =>
  ROOM_SERIALIZERS[format](room, layers);

export const downloadRoomModel = (room: FloorPlanRoom, format: ExportFormat, layers: PlanLayers): void =>
  downloadFile(exportFile(ROOM_EXPORT_NAME, format, serializeRoomModel(room, format, layers)));

/**
 * An assembled level serialized for export: the given rooms (the level on
 * screen) as one plan, 2D through the assembly's own drawing, 3D through the
 * composed level scene, in one file, exactly as the assembler shows them,
 * with what the view menu shows.
 */
export const serializeAssemblyModel = (
  rooms: readonly AssemblyRoom[],
  format: ExportFormat,
  view: PlanViewPreferences = DEFAULT_FLOOR_PLAN_PREFERENCES,
): string | ArrayBuffer => ASSEMBLY_SERIALIZERS[format](rooms, view);

export const downloadAssemblyModel = (rooms: readonly AssemblyRoom[], format: ExportFormat, level: number, view: PlanViewPreferences): void =>
  downloadFile(exportFile(levelExportName(level), format, serializeAssemblyModel(rooms, format, view)));
